import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Package, Target, Users, Layers, AlertTriangle,
  Sparkles, Edit3, FileText, TrendingUp, Scale, Download, Loader2,
  Check, ExternalLink, Link2, GitBranch, FolderGit2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const statusColors = { 
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', 
  in_development: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', 
  maintenance: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', 
  sunset: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400', 
  planned: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
};
const priorityColors = { 
  P0: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', 
  P1: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', 
  P2: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
};
const tshirtColors = { 
  S: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', 
  M: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', 
  L: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', 
  XL: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
};

// Simple markdown renderer for scope content
function MarkdownContent({ content }) {
  if (!content) return null;
  
  const lines = content.split('\n');
  const elements = [];
  let currentList = [];
  let listKey = 0;
  
  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${listKey++}`} className="space-y-2.5 mb-5 ml-1">
          {currentList.map((item, i) => (
            <li key={i} className="text-sm font-light text-[#30302F] dark:text-white/90 flex items-start gap-3 leading-relaxed">
              <span className="text-[#98139C] mt-1 text-base">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };
  
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed) {
      flushList();
      return;
    }
    
    // H2 heading (## Title)
    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h4 key={idx} className="text-base font-semibold text-[#4F0077] dark:text-[#f0b8f3] mt-6 mb-3 first:mt-0">
          {trimmed.replace(/^##\s*/, '').replace(/\*\*/g, '')}
        </h4>
      );
      return;
    }
    
    // H3 heading (### Title)
    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h5 key={idx} className="text-base font-semibold text-black dark:text-white mt-4 mb-2">
          {trimmed.replace(/^###\s*/, '').replace(/\*\*/g, '')}
        </h5>
      );
      return;
    }
    
    // List item (- item or * item)
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const text = trimmed.replace(/^[-*]\s*/, '').replace(/\*\*/g, '');
      currentList.push(text);
      return;
    }
    
    // Bold text line (**text**)
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      flushList();
      elements.push(
        <p key={idx} className="text-base font-semibold text-black dark:text-white mt-4 mb-2">
          {trimmed.replace(/\*\*/g, '')}
        </p>
      );
      return;
    }
    
    // Regular paragraph
    flushList();
    const parts = trimmed.split(/\*\*(.*?)\*\*/g);
    elements.push(
      <p key={idx} className="text-sm font-light text-[#30302F] dark:text-white/90 mb-3 leading-relaxed">
        {parts.map((part, i) =>
          i % 2 === 1 ? <strong key={i}>{part}</strong> : part
        )}
      </p>
    );
  });
  
  flushList();
  return <div className="markdown-content">{elements}</div>;
}

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVision, setEditingVision] = useState(false);
  const [visionInput, setVisionInput] = useState('');
  const [scopeInput, setScopeInput] = useState('');
  
  // New editing states
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionInput, setDescriptionInput] = useState('');
  const [editingTeam, setEditingTeam] = useState(false);
  const [teamInput, setTeamInput] = useState('');
  const [newMember, setNewMember] = useState('');
  
  // Header editing states
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({ name: '', status: '', priority: '' });
  
  // Who We Serve editing state
  const [editingWhoWeServe, setEditingWhoWeServe] = useState(false);
  const [whoWeServeInput, setWhoWeServeInput] = useState('');
  
  // One-Pager states
  const [generatingOnePager, setGeneratingOnePager] = useState(false);
  const [onePager, setOnePager] = useState(null);
  const [showOnePager, setShowOnePager] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Story Detail Modal state
  const [selectedStory, setSelectedStory] = useState(null);
  const [showStoryModal, setShowStoryModal] = useState(false);

  // Code Repository config state
  const [adoRepos, setAdoRepos] = useState([]);
  const [editingRepos, setEditingRepos] = useState(false);
  const [codeReposInput, setCodeReposInput] = useState([]);
  const [docRepoInput, setDocRepoInput] = useState('');
  const [docPathPrefixInput, setDocPathPrefixInput] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [productRes, intakesRes, storiesRes] = await Promise.all([
          api.get(`/products/${productId}`),
          api.get(`/products/${productId}/intakes`),
          api.get(`/products/${productId}/stories`)
        ]);
        setProduct(productRes.data);
        setIntakes(intakesRes.data);
        setStories(storiesRes.data);
        // Load existing one-pager if available
        if (productRes.data.one_pager) {
          setOnePager(productRes.data.one_pager);
        }
        // Load ADO repos for repo config. The endpoint wraps the list as
        // { repositories: [...] }, so unwrap; falling back to .data when a
        // future caller returns a bare array.
        try {
          const reposRes = await api.get('/devops/repositories');
          const list = Array.isArray(reposRes.data)
            ? reposRes.data
            : (reposRes.data?.repositories || []);
          setAdoRepos(list);
        } catch (_) { /* ADO not configured yet */ }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [productId]);

  // One-Pager functions
  const generateOnePager = async () => {
    setGeneratingOnePager(true);
    try {
      const res = await api.post('/ai/one-pager', { product_id: productId });
      setOnePager(res.data);
      setShowOnePager(true);
    } catch (err) {
      console.error(err);
      alert('Failed to generate one-pager: ' + (err.response?.data?.detail || err.message));
    }
    finally { setGeneratingOnePager(false); }
  };

  const saveOnePager = async () => {
    try {
      await api.post(`/ai/one-pager/save?product_id=${productId}`, onePager);
      setProduct({ ...product, one_pager: onePager });
      alert('One-pager saved successfully!');
    } catch (err) {
      alert('Failed to save one-pager');
    }
  };

  // Export One-Pager as PDF
  const [exporting, setExporting] = useState(false);
  
  const exportOnePagerPDF = async () => {
    if (!onePager) return;
    
    setExporting(true);
    try {
      // Create a printable HTML document
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${product?.name} - One-Pager</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #1a1a1a; }
            h1 { font-size: 28px; margin-bottom: 8px; color: #98139C; }
            h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; color: #98139C; margin-bottom: 8px; margin-top: 24px; border-bottom: 2px solid #98139C; padding-bottom: 4px; }
            .subtitle { color: #666; margin-bottom: 24px; font-size: 14px; }
            .section { margin-bottom: 20px; }
            .section-content { color: #333; line-height: 1.6; font-size: 14px; }
            ul { padding-left: 20px; }
            li { margin-bottom: 6px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .card { background: #f8f8f8; padding: 16px; border-radius: 8px; }
            .meta { font-size: 12px; color: #888; margin-top: 32px; padding-top: 16px; border-top: 1px solid #eee; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>${product?.name || 'Product'}</h1>
          <p class="subtitle">${product?.description || ''}</p>
          
          <div class="grid">
            <div class="card">
              <h2>Vision</h2>
              <p class="section-content">${onePager.vision || 'N/A'}</p>
            </div>
            <div class="card">
              <h2>Problem Statement</h2>
              <p class="section-content">${onePager.problem_statement || 'N/A'}</p>
            </div>
          </div>
          
          <div class="grid" style="margin-top: 20px;">
            <div class="card">
              <h2>Target Users</h2>
              <p class="section-content">${onePager.target_users || 'N/A'}</p>
            </div>
            <div class="card">
              <h2>Key Features</h2>
              <ul class="section-content">
                ${(onePager.key_features || []).map(f => `<li>${f}</li>`).join('')}
              </ul>
            </div>
          </div>
          
          <div class="section" style="margin-top: 20px;">
            <h2>Success Metrics</h2>
            <ul class="section-content">
              ${(onePager.success_metrics || []).map(m => `<li>${m}</li>`).join('')}
            </ul>
          </div>
          
          <div class="meta">
            Generated by SPEEDFORCE PM Copilot • ${new Date().toLocaleDateString()}
          </div>
        </body>
        </html>
      `;
      
      // Open print dialog
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load then trigger print
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleUploadOnePager = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/ai/upload-one-pager', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.data) {
        const d = res.data.data;
        // Build full MaaS one-pager structure
        setOnePager({
          vision: d.vision || '',
          problem_statement: d.problem_statement || '',
          target_users: d.target_users || d.who_we_serve || '',
          key_features: d.key_features || [],
          success_metrics: d.success_metrics || [],
          risks_and_mitigations: d.risks_and_mitigations || [],
          // Extended MaaS fields
          where_were_going: d.where_were_going || {},
          who_we_serve: d.who_we_serve || d.target_users || '',
          faqs: d.faqs || [],
          roadmap: d.roadmap || [],
          out_of_scope: d.out_of_scope || []
        });
        // Also update product vision/scope/description if extracted
        const updates = {};
        if (d.vision) updates.vision = d.vision;
        if (d.scope) updates.scope = d.scope;
        if (d.description) updates.description = d.description;
        if (Object.keys(updates).length > 0) {
          const upRes = await api.patch(`/products/${productId}`, updates);
          setProduct(upRes.data);
        }
        setShowOnePager(true);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to upload: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const saveVision = async () => {
    try {
      const updateRes = await api.patch(`/products/${productId}`, {
        vision: visionInput,
        scope: scopeInput
      });
      setProduct(updateRes.data);
      setEditingVision(false);
    } catch (err) { console.error(err); }
  };

  const startEditing = () => {
    setVisionInput(product.vision || '');
    setScopeInput(product.scope || '');
    setEditingVision(true);
  };

  // Description editing
  const startEditingDescription = () => {
    setDescriptionInput(product.description || '');
    setEditingDescription(true);
  };

  const saveDescription = async () => {
    try {
      const res = await api.patch(`/products/${productId}`, { description: descriptionInput });
      setProduct(res.data);
      setEditingDescription(false);
    } catch (err) { console.error(err); }
  };

  // Team editing
  const startEditingTeam = () => {
    setTeamInput(product.team || []);
    setEditingTeam(true);
  };

  const addTeamMember = () => {
    if (newMember.trim() && !teamInput.includes(newMember.trim())) {
      setTeamInput([...teamInput, newMember.trim()]);
      setNewMember('');
    }
  };

  const removeTeamMember = (member) => {
    setTeamInput(teamInput.filter(m => m !== member));
  };

  const saveTeam = async () => {
    try {
      const res = await api.patch(`/products/${productId}`, { team: teamInput });
      setProduct(res.data);
      setEditingTeam(false);
    } catch (err) { console.error(err); }
  };

  // Header editing (title, status, priority)
  const startEditingHeader = () => {
    setHeaderForm({
      name: product.name || '',
      status: product.status || 'active',
      priority: product.priority || 'P1'
    });
    setEditingHeader(true);
  };

  const saveHeader = async () => {
    try {
      const res = await api.patch(`/products/${productId}`, headerForm);
      setProduct(res.data);
      setEditingHeader(false);
    } catch (err) { console.error(err); }
  };

  // Who We Serve editing
  const startEditingWhoWeServe = () => {
    setWhoWeServeInput(product.who_we_serve || onePager?.who_we_serve || onePager?.target_users || '');
    setEditingWhoWeServe(true);
  };

  const saveWhoWeServe = async () => {
    try {
      const res = await api.patch(`/products/${productId}`, { who_we_serve: whoWeServeInput });
      setProduct(res.data);
      setEditingWhoWeServe(false);
    } catch (err) { console.error(err); }
  };

  // Code Repos editing
  const startEditingRepos = () => {
    setCodeReposInput(product.code_repos || []);
    setDocRepoInput(product.doc_repo || '');
    setDocPathPrefixInput(product.doc_path_prefix || 'docs/');
    setEditingRepos(true);
  };

  const toggleCodeRepo = (repoId) => {
    setCodeReposInput(prev =>
      prev.includes(repoId) ? prev.filter(r => r !== repoId) : [...prev, repoId]
    );
  };

  const saveRepoConfig = async () => {
    try {
      const res = await api.patch(`/products/${productId}`, {
        code_repos: codeReposInput,
        doc_repo: docRepoInput || null,
        doc_path_prefix: docPathPrefixInput || 'docs/'
      });
      setProduct(res.data);
      setEditingRepos(false);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="p-8 text-[#30302F] dark:text-white">Loading...</div>;
  if (!product) return <div className="p-8 text-[#30302F] dark:text-white">Product not found</div>;

  return (
    <div className="p-4 lg:p-8" data-testid="product-detail-page">
      <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-[#98139C] hover:underline font-medium mb-6" data-testid="back-to-dashboard">
        <ArrowLeft size={16} /> Back to Dashboard
      </button>

      {/* Product Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-[#f0dcf0] dark:bg-[#98139C]/20 flex items-center justify-center">
            <Package size={28} className="text-[#98139C]" />
          </div>
          
          {editingHeader ? (
            <div className="space-y-3">
              <input
                type="text"
                value={headerForm.name}
                onChange={(e) => setHeaderForm({ ...headerForm, name: e.target.value })}
                className="text-2xl font-heading text-[#30302F] dark:text-white bg-transparent border-b-2 border-[#98139C] focus:outline-none w-64"
                placeholder="Product name"
                data-testid="edit-product-name"
              />
              <div className="flex items-center gap-3">
                <select
                  value={headerForm.status}
                  onChange={(e) => setHeaderForm({ ...headerForm, status: e.target.value })}
                  className="text-xs px-2 py-1 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white focus:outline-none focus:border-[#98139C]"
                  data-testid="edit-product-status"
                >
                  <option value="active">Active</option>
                  <option value="in_development">In Development</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="sunset">Sunset</option>
                  <option value="planned">Planned</option>
                </select>
                <select
                  value={headerForm.priority}
                  onChange={(e) => setHeaderForm({ ...headerForm, priority: e.target.value })}
                  className="text-xs px-2 py-1 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white focus:outline-none focus:border-[#98139C]"
                  data-testid="edit-product-priority"
                >
                  <option value="P0">P0</option>
                  <option value="P1">P1</option>
                  <option value="P2">P2</option>
                </select>
                <button onClick={saveHeader} className="text-xs bg-[#98139C] text-white px-3 py-1 rounded-sm font-medium" data-testid="save-header-btn">Save</button>
                <button onClick={() => setEditingHeader(false)} className="text-xs text-[#30302F]/80 dark:text-white/80">Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-heading text-[#30302F] dark:text-white" style={{ letterSpacing: '-0.6px' }}>{product.name}</h1>
                <button onClick={startEditingHeader} className="text-[#98139C] hover:text-[#7a0f7d] p-1" data-testid="edit-header-btn">
                  <Edit3 size={14} />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <Badge className={`text-[11px] rounded-none ${statusColors[product.status] || statusColors.active}`}>
                  {product.status?.replace('_', ' ')}
                </Badge>
                <Badge className={`text-[11px] rounded-none ${priorityColors[product.priority]}`}>
                  {product.priority}
                </Badge>
              </div>
            </div>
          )}
        </div>
        
        {/* Action Buttons - Top Right */}
        <div className="flex items-center gap-2">
          {/* One-Pager button */}
          <button
            onClick={() => onePager ? setShowOnePager(!showOnePager) : generateOnePager()}
            disabled={generatingOnePager}
            className="h-9 px-4 text-sm bg-[#98139C] text-white hover:bg-[#7a0f7d] flex items-center gap-1.5 transition-colors disabled:opacity-50 rounded-full font-medium"
            data-testid="one-pager-btn"
          >
            {generatingOnePager ? (
              <><Loader2 size={14} className="animate-spin" /> Generating...</>
            ) : onePager ? (
              <><FileText size={14} /> {showOnePager ? 'Hide' : 'View'} One-Pager</>
            ) : (
              <><Sparkles size={14} /> Generate One-Pager</>
            )}
          </button>
          
          {/* Upload One-Pager */}
          <label className="h-9 px-4 text-sm border border-[#98139C] text-[#98139C] hover:bg-[#98139C]/10 flex items-center gap-1.5 transition-colors cursor-pointer rounded-full font-medium" data-testid="upload-one-pager-btn">
            {uploading ? (
              <><Loader2 size={14} className="animate-spin" /> Uploading...</>
            ) : (
              <><FileText size={14} /> Upload</>
            )}
            <input type="file" accept=".pdf,.docx" onChange={handleUploadOnePager} className="hidden" disabled={uploading} />
          </label>
          
          {/* Export PDF (only show if one-pager exists) */}
          {onePager && (
            <button
              onClick={exportOnePagerPDF}
              disabled={exporting}
              className="h-9 px-4 text-sm border border-[#98139C] text-[#98139C] hover:bg-[#98139C]/10 flex items-center gap-1.5 transition-colors rounded-full font-medium"
              data-testid="export-pdf-btn"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export PDF
            </button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1">Total Size</div>
          <div className="text-2xl font-bold text-[#98139C] dark:text-[#d87bdb]">{product.total_story_points || 0} pts</div>
          <div className="text-xs text-[#30302F]/70 dark:text-white/70 mt-1">{product.story_count || 0} stories</div>
        </div>
        
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1 flex items-center gap-1">
            <Layers size={10} /> Intakes
          </div>
          <div className="text-2xl font-medium text-[#30302F] dark:text-white">{intakes.length}</div>
          <div className="text-xs text-[#30302F]/70 dark:text-white/70 mt-1">Linked items</div>
        </div>
        
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white flex items-center gap-1">
              <Users size={10} /> Team
            </div>
            {!editingTeam && (
              <button onClick={startEditingTeam} className="text-[11px] text-[#98139C] hover:underline" data-testid="edit-team-btn">
                <Edit3 size={10} />
              </button>
            )}
          </div>
          {editingTeam ? (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap gap-1">
                {teamInput.map((member, i) => (
                  <span key={i} className="text-[11px] bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] px-2 py-0.5 flex items-center gap-1">
                    {member}
                    <button onClick={() => removeTeamMember(member)} className="hover:text-red-500">×</button>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={newMember}
                  onChange={(e) => setNewMember(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTeamMember()}
                  placeholder="Add member..."
                  className="flex-1 h-6 px-2 text-[11px] border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-black dark:text-white focus:outline-none focus:border-[#98139C]"
                  data-testid="add-member-input"
                />
                <button onClick={addTeamMember} className="text-[11px] text-[#98139C] font-bold">+</button>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={saveTeam} className="text-[11px] bg-[#98139C] text-white px-2 py-0.5 rounded-sm" data-testid="save-team-btn">Save</button>
                <button onClick={() => setEditingTeam(false)} className="text-[11px] text-[#30302F] dark:text-white">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              {(() => {
                // Merge manually-added team and assignees pulled from synced
                // stories so ADO-imported products surface real contributors
                // instead of an empty list.
                const merged = Array.from(new Set([
                  ...(product.team || []),
                  ...(product.team_from_assignees || []),
                ])).filter(Boolean);
                return (
                  <>
                    <div className="text-2xl font-medium text-[#30302F] dark:text-white">{merged.length}</div>
                    <div className="text-xs text-[#30302F]/70 dark:text-white/70 mt-1 truncate" title={merged.join(', ')}>
                      {merged.slice(0, 3).join(', ')}{merged.length > 3 ? ` +${merged.length - 3} more` : ''}
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
        
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1">Status</div>
          <Badge className={`text-xs ${statusColors[product.status] || statusColors.active}`}>
            {product.status?.replace('_', ' ') || 'Active'}
          </Badge>
          <div className="text-xs text-[#30302F]/70 dark:text-white/70 mt-2">
            Created {new Date(product.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* One-Pager Panel */}
      {showOnePager && onePager && (
        <div className="bg-white dark:bg-white/5 border-2 border-[#98139C]/30 p-6 mb-6" data-testid="one-pager-panel">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <FileText size={24} className="text-[#98139C]" />
              <h2 className="text-xl font-heading text-[#30302F] dark:text-white">Product One-Pager</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={generateOnePager}
                disabled={generatingOnePager}
                className="text-xs text-[#98139C] hover:underline flex items-center gap-1"
              >
                <Sparkles size={12} /> Regenerate
              </button>
              <button
                onClick={exportOnePagerPDF}
                disabled={exporting}
                className="text-xs border border-[#98139C] text-[#98139C] px-3 py-1.5 hover:bg-[#98139C]/10 flex items-center gap-1"
              >
                {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                Export PDF
              </button>
              <button
                onClick={saveOnePager}
                className="text-xs bg-[#98139C] text-white px-3 py-1.5 hover:bg-[#7a0f7d] flex items-center gap-1"
              >
                Save to Product
              </button>
              <button onClick={() => setShowOnePager(false)} className="text-[#30302F] hover:text-[#30302F] dark:text-white dark:hover:text-white ml-2">×</button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Vision */}
            <div className="p-4 bg-[#98139C]/5 border border-[#98139C]/20 rounded">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-[#98139C] mb-2 flex items-center gap-1">
                <Target size={12} /> Vision
              </h4>
              <p className="text-sm text-[#30302F] dark:text-white leading-relaxed">{onePager.vision}</p>
            </div>
            
            {/* Problem Statement */}
            <div className="p-4 bg-[#98139C]/5 border border-[#98139C]/20 rounded">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-[#98139C] mb-2 flex items-center gap-1">
                <AlertTriangle size={12} /> Problem Statement
              </h4>
              <p className="text-sm text-[#30302F] dark:text-white leading-relaxed">{onePager.problem_statement}</p>
            </div>
            
            {/* Target Users / Who We Serve */}
            <div className="p-4 bg-[#98139C]/5 border border-[#98139C]/20 rounded">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-[#98139C] mb-2 flex items-center gap-1">
                <Users size={12} /> Who We Serve
              </h4>
              <p className="text-sm text-[#30302F] dark:text-white leading-relaxed">{onePager.who_we_serve || onePager.target_users}</p>
            </div>
            
            {/* Key Features */}
            <div className="p-4 bg-[#98139C]/5 border border-[#98139C]/20 rounded">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-[#98139C] mb-2 flex items-center gap-1">
                <Layers size={12} /> Key Features
              </h4>
              <ul className="space-y-1">
                {onePager.key_features?.map((feature, i) => (
                  <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                    <span className="text-[#98139C] mt-1">•</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Success Metrics */}
            <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 rounded">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
                <TrendingUp size={12} /> Success Metrics
              </h4>
              <ul className="space-y-1">
                {onePager.success_metrics?.map((metric, i) => (
                  <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                    <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                    {metric}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Risks & Mitigations */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1">
                <AlertTriangle size={12} /> Risks & Mitigations
              </h4>
              <ul className="space-y-1">
                {onePager.risks_and_mitigations?.map((risk, i) => (
                  <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                    <span className="text-amber-600 dark:text-amber-400 mt-1">⚠</span>
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* FAQs Section - Full width below grid */}
          {onePager.faqs && onePager.faqs.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#98139C]/20">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-[#98139C] mb-4">
                Frequently Asked Questions ({onePager.faqs.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {onePager.faqs.map((faq, i) => (
                  <div key={i} className="p-3 bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded">
                    <p className="text-sm font-semibold text-[#30302F] dark:text-white mb-1">
                      {faq.question}
                    </p>
                    <p className="text-sm text-[#30302F]/80 dark:text-white/80">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Roadmap Section */}
          {onePager.roadmap && onePager.roadmap.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#98139C]/20">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-[#98139C] mb-4">
                Roadmap Initiatives
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#faf8fb] dark:bg-white/5">
                      <th className="text-left p-2 font-medium text-[#30302F]/80 dark:text-white/80">Initiative</th>
                      <th className="text-left p-2 font-medium text-[#30302F]/80 dark:text-white/80">Priority</th>
                      <th className="text-left p-2 font-medium text-[#30302F]/80 dark:text-white/80">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onePager.roadmap.map((item, i) => (
                      <tr key={i} className="border-b border-[#e3e3e3] dark:border-white/10">
                        <td className="p-2 text-[#30302F] dark:text-white">{item.initiative}</td>
                        <td className="p-2">
                          <Badge className={`text-[11px] rounded-none ${
                            item.priority === 'P0' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            item.priority === 'P1' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                          }`}>
                            {item.priority}
                          </Badge>
                        </td>
                        <td className="p-2 text-[#30302F]/80 dark:text-white/80">{item.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Out of Scope */}
          {onePager.out_of_scope && onePager.out_of_scope.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#98139C]/20">
              <h4 className="text-[11px] uppercase tracking-wide font-bold text-gray-500 mb-3">
                Out of Scope
              </h4>
              <div className="flex flex-wrap gap-2">
                {onePager.out_of_scope.map((item, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/70 rounded">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Description, Who We Serve, Vision, Scope - Vertical Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Description */}
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs uppercase tracking-[1px] font-bold text-[#98139C] dark:text-[#f0b8f3]">Description</h3>
            {!editingDescription && (
              <button onClick={startEditingDescription} className="text-xs text-[#98139C] hover:underline font-medium flex items-center gap-1" data-testid="edit-description-btn">
                <Edit3 size={12} /> Edit
              </button>
            )}
          </div>
          {editingDescription ? (
            <div className="space-y-3">
              <textarea
                value={descriptionInput}
                onChange={(e) => setDescriptionInput(e.target.value)}
                placeholder="Enter product description..."
                className="w-full h-24 p-3 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-sm font-light text-[#30302F] dark:text-white/90 placeholder:text-gray-400 dark:placeholder:text-white/40 focus:outline-none focus:border-[#98139C]"
                data-testid="description-input"
              />
              <div className="flex items-center gap-2">
                <button onClick={saveDescription} className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-xs px-4 h-8 transition-colors" data-testid="save-description-btn">Save</button>
                <button onClick={() => setEditingDescription(false)} className="text-[#30302F] dark:text-white hover:text-[#30302F] dark:hover:text-white text-xs font-medium">Cancel</button>
              </div>
            </div>
          ) : (
            <p className="text-sm font-light text-[#30302F] dark:text-white/90 leading-relaxed">
              {product.description || <span className="italic text-gray-500 dark:text-gray-400">No description yet. Click Edit to add one.</span>}
            </p>
          )}
        </div>

        {/* Who We Serve */}
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs uppercase tracking-[1px] font-bold text-[#98139C] dark:text-[#f0b8f3] flex items-center gap-1.5">
              <Users size={14} /> Who We Serve
            </h3>
            {!editingWhoWeServe && (
              <button onClick={startEditingWhoWeServe} className="text-xs text-[#98139C] hover:underline font-medium flex items-center gap-1" data-testid="edit-who-we-serve-btn">
                <Edit3 size={12} /> Edit
              </button>
            )}
          </div>
          {editingWhoWeServe ? (
            <div className="space-y-3">
              <textarea
                value={whoWeServeInput}
                onChange={(e) => setWhoWeServeInput(e.target.value)}
                placeholder="Describe your target users, their needs, and how they benefit from this product..."
                className="w-full h-24 p-3 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-sm font-light text-[#30302F] dark:text-white/90 placeholder:text-gray-400 dark:placeholder:text-white/40 focus:outline-none focus:border-[#98139C]"
                data-testid="who-we-serve-input"
              />
              <div className="flex items-center gap-2">
                <button onClick={saveWhoWeServe} className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-xs px-4 h-8 transition-colors" data-testid="save-who-we-serve-btn">Save</button>
                <button onClick={() => setEditingWhoWeServe(false)} className="text-[#30302F] dark:text-white hover:text-[#30302F] dark:hover:text-white text-xs font-medium">Cancel</button>
              </div>
            </div>
          ) : (
            <p className="text-sm font-light text-[#30302F] dark:text-white/90 leading-relaxed">
              {product.who_we_serve || onePager?.who_we_serve || onePager?.target_users || (
                <span className="italic text-gray-500 dark:text-gray-400">
                  No target users defined yet. Click Edit to add, or upload a one-pager to extract this information.
                </span>
              )}
            </p>
          )}
        </div>

        {/* Vision Card */}
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs uppercase tracking-[1px] font-bold text-[#98139C] dark:text-[#f0b8f3] flex items-center gap-1.5">
              <Target size={14} /> Vision
            </h3>
            {!editingVision && (
              <button onClick={startEditing} className="text-xs text-[#98139C] hover:underline font-medium flex items-center gap-1" data-testid="edit-vision-btn">
                <Edit3 size={12} /> Edit
              </button>
            )}
          </div>
          {editingVision ? (
            <div className="space-y-3">
              <textarea
                value={visionInput}
                onChange={(e) => setVisionInput(e.target.value)}
                placeholder="Enter product vision..."
                className="w-full h-24 p-3 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-sm font-light text-[#30302F] dark:text-white/90 placeholder:text-gray-400 dark:placeholder:text-white/40 focus:outline-none focus:border-[#98139C]"
                data-testid="vision-input"
              />
            </div>
          ) : (
            <p className="text-sm font-light text-[#30302F] dark:text-white/90 leading-relaxed">
              {product.vision || <span className="italic text-gray-500 dark:text-gray-400">No vision defined yet. Click Edit to add one.</span>}
            </p>
          )}
        </div>
        
        {/* Scope Card */}
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs uppercase tracking-[1px] font-bold text-[#98139C] dark:text-[#f0b8f3]">Scope</h3>
            {!editingVision && (
              <button onClick={startEditing} className="text-xs text-[#98139C] hover:underline font-medium flex items-center gap-1" data-testid="edit-scope-btn">
                <Edit3 size={12} /> Edit
              </button>
            )}
          </div>
          {editingVision ? (
            <div className="space-y-3">
              <textarea
                value={scopeInput}
                onChange={(e) => setScopeInput(e.target.value)}
                placeholder="Define product scope..."
                className="w-full h-24 p-3 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-sm font-light text-[#30302F] dark:text-white/90 placeholder:text-gray-400 dark:placeholder:text-white/40 focus:outline-none focus:border-[#98139C]"
                data-testid="scope-input"
              />
              <div className="flex items-center gap-2">
                <button onClick={saveVision} className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-xs px-4 h-8 transition-colors" data-testid="save-vision-btn">Save</button>
                <button onClick={() => setEditingVision(false)} className="text-[#30302F] dark:text-white hover:text-[#30302F] dark:hover:text-white text-xs font-medium">Cancel</button>
              </div>
            </div>
          ) : product.scope ? (
            <MarkdownContent content={product.scope} />
          ) : (
            <p className="text-sm italic text-gray-500 dark:text-gray-400">No scope defined yet. Click Edit to add one.</p>
          )}
        </div>
      </div>

      {/* Code Repositories */}
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 mb-8" data-testid="repo-config-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs uppercase tracking-[1px] font-bold text-[#98139C] dark:text-[#f0b8f3] flex items-center gap-1.5">
            <FolderGit2 size={14} /> Code Repositories
          </h3>
          {!editingRepos && (
            <button onClick={startEditingRepos} className="text-xs text-[#98139C] hover:underline font-medium flex items-center gap-1" data-testid="edit-repos-btn">
              <Edit3 size={12} /> Edit
            </button>
          )}
        </div>

        {editingRepos ? (
          <div className="space-y-5">
            {/* Code Repos multi-select */}
            <div>
              <label className="text-xs font-medium text-[#30302F] dark:text-white/80 mb-2 block">Code Repositories (select multiple)</label>
              {adoRepos.length === 0 ? (
                <p className="text-xs italic text-gray-500 dark:text-gray-400">No repositories found. Connect Azure DevOps in ADO Settings first.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto border border-[#e3e3e3] dark:border-white/10 p-2">
                  {adoRepos.map(repo => (
                    <label key={repo.id} className="flex items-center gap-2 p-1.5 hover:bg-[#f9f5fa] dark:hover:bg-white/5 cursor-pointer text-sm text-[#30302F] dark:text-white">
                      <input
                        type="checkbox"
                        checked={codeReposInput.includes(repo.id)}
                        onChange={() => toggleCodeRepo(repo.id)}
                        className="accent-[#98139C]"
                      />
                      <GitBranch size={12} className="text-[#98139C] shrink-0" />
                      <span>{repo.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Doc Repo single-select */}
            <div>
              <label className="text-xs font-medium text-[#30302F] dark:text-white/80 mb-2 block">Documentation Repository</label>
              <select
                value={docRepoInput}
                onChange={(e) => setDocRepoInput(e.target.value)}
                className="w-full h-9 px-3 text-sm border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white focus:outline-none focus:border-[#98139C]"
                data-testid="doc-repo-select"
              >
                <option value="">None</option>
                {adoRepos.map(repo => (
                  <option key={repo.id} value={repo.id}>{repo.name}</option>
                ))}
              </select>
            </div>

            {/* Doc path prefix */}
            {docRepoInput && (
              <div>
                <label className="text-xs font-medium text-[#30302F] dark:text-white/80 mb-2 block">Documentation Path Prefix</label>
                <input
                  type="text"
                  value={docPathPrefixInput}
                  onChange={(e) => setDocPathPrefixInput(e.target.value)}
                  placeholder="docs/spikes/"
                  className="w-full h-9 px-3 text-sm border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white focus:outline-none focus:border-[#98139C]"
                  data-testid="doc-path-prefix-input"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <button onClick={saveRepoConfig} className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-xs px-4 h-8 transition-colors" data-testid="save-repos-btn">Save</button>
              <button onClick={() => setEditingRepos(false)} className="text-[#30302F] dark:text-white hover:text-[#30302F] dark:hover:text-white text-xs font-medium">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Code repos display */}
            {product.code_repos?.length > 0 ? (
              <div>
                <span className="text-xs text-[#30302F]/70 dark:text-white/70 block mb-1">Code</span>
                <div className="flex flex-wrap gap-2">
                  {product.code_repos.map(repoId => {
                    const repo = adoRepos.find(r => r.id === repoId);
                    return (
                      <span key={repoId} className="text-xs bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] px-2 py-1 flex items-center gap-1">
                        <GitBranch size={10} /> {repo?.name || repoId}
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-sm italic text-gray-500 dark:text-gray-400">No code repositories linked. Click Edit to configure.</p>
            )}

            {/* Doc repo display */}
            {product.doc_repo && (
              <div>
                <span className="text-xs text-[#30302F]/70 dark:text-white/70 block mb-1">Documentation</span>
                <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-1 inline-flex items-center gap-1">
                  <FileText size={10} /> {adoRepos.find(r => r.id === product.doc_repo)?.name || product.doc_repo}
                  {product.doc_path_prefix && <span className="text-blue-500/70 ml-1">({product.doc_path_prefix})</span>}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Current state snapshot — replaces the flat Intakes + Stories
          dumps with a small "what's happening now" view. The Backlog page
          covers the full enumeration with hierarchy, filtering, drag-and-
          drop reorder, so duplicating it inline added no value. */}
      {(() => {
        const inProgress = stories.filter(s => s.state === 'IN_PROGRESS').slice(0, 6);
        const blocked = stories.filter(s => s.state === 'BLOCKED' || (s.tags || []).includes('blocked')).slice(0, 4);
        const recentlyDone = stories
          .filter(s => s.state === 'DONE')
          .slice()
          .sort((a, b) => (b.ado_synced_at || b.created_at || '').localeCompare(a.ado_synced_at || a.created_at || ''))
          .slice(0, 5);

        const StoryRow = ({ story }) => (
          <div
            key={story.id}
            onClick={() => { setSelectedStory(story); setShowStoryModal(true); }}
            className="flex items-center justify-between gap-4 py-2.5 px-3 rounded-lg hover:bg-[#f9f5fa] dark:hover:bg-white/5 cursor-pointer"
            data-testid={`story-row-${story.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[#30302F] dark:text-white truncate">{story.title}</div>
              <div className="text-xs text-[#30302F]/70 dark:text-white/60 mt-0.5 truncate">
                {story.assignee || 'Unassigned'}
                {story.ado_id ? <span className="text-[#30302F]/40 dark:text-white/40"> &middot; #{story.ado_id}</span> : null}
                {story.story_points ? <span className="text-[#30302F]/40 dark:text-white/40"> &middot; {story.story_points} pts</span> : null}
              </div>
            </div>
          </div>
        );

        return (
          <div className="space-y-8">
            {/* CTA strip */}
            <div className="flex items-center justify-between gap-4 bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl px-5 py-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#30302F] dark:text-white">Full backlog for {product.name}</div>
                <div className="text-xs text-[#30302F]/70 dark:text-white/60 mt-0.5">
                  {product.intake_count || intakes.length} items &middot; {stories.length} stories &middot; {product.total_story_points || 0} story points
                </div>
              </div>
              <button
                onClick={() => navigate(`/backlog?product=${encodeURIComponent(product.name)}`)}
                className="inline-flex items-center gap-2 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-10 transition-colors shrink-0"
              >
                Open Backlog
              </button>
            </div>

            {/* In Progress */}
            <section className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp size={18} className="text-[#98139C]" />
                <h2 className="text-base font-semibold text-[#30302F] dark:text-white">In progress</h2>
                <span className="text-xs text-[#30302F]/60 dark:text-white/60 font-light">
                  {stories.filter(s => s.state === 'IN_PROGRESS').length} active
                </span>
              </div>
              {inProgress.length === 0 ? (
                <p className="text-sm text-[#30302F]/60 dark:text-white/60 italic">No stories in flight right now.</p>
              ) : (
                <div className="divide-y divide-[#e3e3e3] dark:divide-white/5">
                  {inProgress.map(s => <StoryRow key={s.id} story={s} />)}
                </div>
              )}
            </section>

            {/* Blocked */}
            {blocked.length > 0 && (
              <section className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <FileText size={18} className="text-amber-600 dark:text-amber-400" />
                  <h2 className="text-base font-semibold text-amber-900 dark:text-amber-200">Blocked</h2>
                  <span className="text-xs text-amber-700/80 dark:text-amber-300/70 font-light">{blocked.length}</span>
                </div>
                <div className="divide-y divide-amber-200/60 dark:divide-amber-700/20">
                  {blocked.map(s => <StoryRow key={s.id} story={s} />)}
                </div>
              </section>
            )}

            {/* Recently done */}
            {recentlyDone.length > 0 && (
              <section className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp size={18} className="text-emerald-600 dark:text-emerald-400" />
                  <h2 className="text-base font-semibold text-[#30302F] dark:text-white">Recently done</h2>
                  <span className="text-xs text-[#30302F]/60 dark:text-white/60 font-light">latest {recentlyDone.length}</span>
                </div>
                <div className="divide-y divide-[#e3e3e3] dark:divide-white/5">
                  {recentlyDone.map(s => <StoryRow key={s.id} story={s} />)}
                </div>
              </section>
            )}
          </div>
        );
      })()}

      {/* Story Detail Modal */}
      <Dialog open={showStoryModal} onOpenChange={setShowStoryModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white" style={{ letterSpacing: '-0.4px' }}>
              Story Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedStory && (
            <div className="space-y-6 mt-4">
              {/* Title & ADO ID */}
              <div>
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-lg font-medium text-[#30302F] dark:text-white">{selectedStory.title}</h3>
                  {selectedStory.ado_story_id && (
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-1 font-mono shrink-0">
                      ADO #{selectedStory.ado_story_id}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <Badge className={`text-[11px] rounded-none ${
                    selectedStory.state === 'DONE' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                    selectedStory.state === 'IN_PROGRESS' ? 'bg-[#f0dcf0] text-[#4F0077] dark:bg-[#98139C]/20 dark:text-[#d87bdb]' :
                    'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  }`}>{selectedStory.state?.replace(/_/g, ' ')}</Badge>
                  {selectedStory.story_points && <span className="text-xs font-bold bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] px-2 py-0.5">{selectedStory.story_points} pts</span>}
                  {selectedStory.t_shirt_size && <Badge className={`text-[11px] rounded-none ${tshirtColors[selectedStory.t_shirt_size]}`}>{selectedStory.t_shirt_size}</Badge>}
                </div>
              </div>

              {/* Azure DevOps Link */}
              {selectedStory.ado_url ? (
                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700/30 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Link2 size={16} className="text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Azure DevOps Work Item</span>
                    </div>
                    <a 
                      href={selectedStory.ado_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      Open in ADO <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-white/80">
                    <Link2 size={16} />
                    <span className="text-sm">Not synced to Azure DevOps</span>
                  </div>
                </div>
              )}

              {/* User Story Details */}
              <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">User Story</h4>
                <p className="text-sm text-[#30302F] dark:text-white">
                  <span className="font-medium">As a</span> {selectedStory.as_a || '—'}, <span className="font-medium">I want</span> {selectedStory.i_want || '—'}, <span className="font-medium">so that</span> {selectedStory.so_that || '—'}
                </p>
              </div>

              {/* Acceptance Criteria */}
              {selectedStory.acceptance_criteria?.length > 0 && (
                <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                  <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">Acceptance Criteria</h4>
                  <ul className="space-y-1">
                    {selectedStory.acceptance_criteria.map((ac, i) => (
                      <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                        <Check size={14} className="text-green-500 mt-0.5 shrink-0" />
                        <span>{ac}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* RICE Scores */}
              {selectedStory.rice_score && (
                <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                  <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">RICE Score</h4>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <span className="text-2xl font-bold text-[#98139C]">{Math.round(selectedStory.rice_score)}</span>
                      <p className="text-[11px] text-[#30302F] dark:text-white">Score</p>
                    </div>
                    <div className="flex-1 grid grid-cols-4 gap-4 text-center">
                      <div>
                        <span className="text-sm font-medium text-[#30302F] dark:text-white">{selectedStory.rice_reach || '—'}</span>
                        <p className="text-[11px] text-[#30302F] dark:text-white">Reach</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-[#30302F] dark:text-white">{selectedStory.rice_impact || '—'}</span>
                        <p className="text-[11px] text-[#30302F] dark:text-white">Impact</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-[#30302F] dark:text-white">{selectedStory.rice_confidence || '—'}</span>
                        <p className="text-[11px] text-[#30302F] dark:text-white">Confidence</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-[#30302F] dark:text-white">{selectedStory.rice_effort || '—'}</span>
                        <p className="text-[11px] text-[#30302F] dark:text-white">Effort</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
